import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import { FormInputWrapper } from "@insite/mobius/FormField";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, StyledTypography } from "@insite/mobius/Link";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TooltipPresentationProps } from "@waytek/MobiusOverrides/Tooltip/Tooltip";
import ProductAvailability, { ProductAvailabilityStyles } from "@waytek/Overrides/Components/ProductAvailability";
import CartLineQuantity, { CartLineQuantityStyles } from "@waytek/Overrides/Widgets/Cart/CartLineQuantity";
import removeCartLine from "@waytek/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@waytek/Store/Pages/Cart/Handlers/UpdateCartLine";
import displayOrder from "@waytek/Store/Pages/SavedOrderDetails/Handlers/ForceDisplayOrder";
import { clearBtnStyle, removeBtnFocusBorder } from "@waytek/Utilities/styleguide/btnIcon";
import { colorPalette, fontFamily, mediaQueriesBP } from "@waytek/Utilities/variables";
import CustomerNumberComponent, { CustomerNumberComponentStyles } from "@waytek/Widgets/Custom/CustomerNumberComponent";
import ScheduleShipmentDetailComponent, {
    ScheduleShipmentDetailStyles,
} from "@waytek/Widgets/Custom/ScheduleShipmentDetailComponent";
import ScheduleShipmentRenderComponent from "@waytek/Widgets/Custom/ScheduleShipmentRenderComponent";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    extendedStyles?: SavedOrderDetailsCartLineStyles;
    handleScheduleShipmentOpen: (cartLine: CartLineModel) => void;
}

const mapStateToProps = (state: ApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    const {
        wishListSettings: { allowMultipleWishLists },
        productSettings: { enableVat, vatPriceDisplay },
    } = settingsCollection;
    const { customSettings } = settingsCollection as any;

    return {
        allowMultipleWishLists,
        enableVat,
        vatPriceDisplay,
        customSettings: customSettings as any,
        showPricing: state.context.showPricing,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    updateCartLine,
    removeCartLine,
    displayOrder,
};

type Props = OwnProps &
    HasCartLineContext &
    HasToasterContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderDetailsCartLineStyles {
    container?: GridContainerProps;
    imageItem?: GridItemProps;
    imageLink?: LinkPresentationProps;
    image?: LazyImageProps;
    infoItem?: GridItemProps;
    infoInnerContainer?: GridContainerProps;
    infoLeftColumn?: GridItemProps;
    infoLeftColumnContainer?: GridContainerProps;
    brandItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    productNameItem?: GridItemProps;
    productNameLink?: LinkPresentationProps;
    numbersItem?: GridItemProps;
    numbersInnerContainer?: GridContainerProps;
    partItem?: GridItemProps;
    customerItem?: GridItemProps;
    partStyles?: SmallHeadingAndTextStyles;
    myPartItem?: GridItemProps;
    myPartStyles?: SmallHeadingAndTextStyles;
    mgfItem?: GridItemProps;
    mgfStyles?: SmallHeadingAndTextStyles;
    notesItem?: GridItemProps;
    notesStyles?: SmallHeadingAndTextStyles;
    infoRightColumn?: GridItemProps;
    infoRightColumnContainer?: GridContainerProps;
    priceItem?: GridItemProps;
    priceStyles?: SmallHeadingAndTextStyles;
    qtyItem?: GridItemProps;
    qtyStyles?: SmallHeadingAndTextStyles;
    totalItem?: GridItemProps;
    totalStyles?: SmallHeadingAndTextStyles;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productDescriptionText?: TypographyPresentationProps;
    savedOrderLineCardAddToListGridItem?: GridItemProps;
    savedOrderLineCardAddToListButton?: ButtonPresentationProps;
    productAvailabilityGridItem?: GridItemProps;
    productAvailability?: ProductAvailabilityStyles;
    mainInfoItem?: GridItemProps;
    secondaryInfoItem?: GridItemProps;
    mobileOnly?: GridItemProps;
    desktopOnly?: GridItemProps;
    customerLabel?: InjectableCss;
    customerWrapper?: InjectableCss;
    schedulesShipmentLinkWrapper?: InjectableCss;
    schedulesShipmentLink?: LinkPresentationProps;
    tooltip?: TooltipPresentationProps;
    scheduleShipmentLinkMobile?: InjectableCss;
    customerNumber?: CustomerNumberComponentStyles;
    scheduleShipmentDetail?: ScheduleShipmentDetailStyles;
    cartLineQty?: CartLineQuantityStyles;
}

export const savedOrderDetailsCartLineStyles: SavedOrderDetailsCartLineStyles = {
    container: {
        css: css`
            padding: 25px 10px;
            border-bottom: solid 1px ${colorPalette.common.global.industrialTaupe};

            @media print {
                padding: 16px 10px 0;
                break-inside: avoid;
            }
        `,
    },
    imageItem: {
        width: 12,
        printWidth: 2,
        css: css`
            flex-direction: column;
            padding-right: 15px;
            flex-basis: 90px;
            max-width: 90px;

            ${mediaQueriesBP.minMD} {
                padding-right: 0;
                flex-basis: 170px;
                max-width: 170px;
            }

            @media print {
                max-width: inherit;
            }
        `,
    },
    imageLink: {
        css: css`
            ${clearBtnStyle}

            background-color: ${colorPalette.common.global.white};
            justify-content: center;
            width: 100%;
            overflow: hidden;
            border: 0;
            border: solid 1px ${colorPalette.common.global.industrialTaupe} !important;
            height: 50px;

            ${StyledButton} {
                ${clearBtnStyle}
                ${removeBtnFocusBorder}
            }

            * {
                height: 100%;
                border: 0 !important;
            }

            ${mediaQueriesBP.minMD} {
                height: 120px;
            }

            @media print {
                height: 70px;
            }
        `,
    },
    image: {
        css: css`
            height: 100%;
            width: 100%;

            img {
                aspect-ratio: 4/3;
                object-fit: contain;
            }
        `,
    },
    infoItem: {
        width: 12,
        css: css`
            flex-basis: calc(100% - 90px);
            max-width: calc(100% - 90px);
            flex-direction: column;

            ${mediaQueriesBP.minMD} {
                flex-basis: calc(100% - 170px);
                max-width: calc(100% - 170px);
                flex-direction: row;
            }
        `,
    },
    infoInnerContainer: {
        gap: 8,
    },
    infoLeftColumn: {
        width: 12,
    },
    infoLeftColumnContainer: {
        gap: 6,
    },
    brandItem: {
        width: 12,
    },
    brandStyles: {
        nameLink: {
            css: css`
                width: auto;
            `,
        },
        nameText: {
            css: css`
                width: auto;
            `,
        },
    },
    productNameItem: {
        width: 12,
    },
    numbersItem: {
        width: 12,
        css: css`
            ${mediaQueriesBP.maxMD} {
                padding: 0 15px;
                min-width: 100%;
            }
        `,
    },
    numbersInnerContainer: {
        gap: 3,
    },
    partItem: {
        width: 12,
        css: css`
            ${mediaQueriesBP.maxSM} {
                min-width: 100%;
            }
        `,
    },
    customerItem: {
        width: 12,
        printWidth: 0,
        css: css`
            @media print {
                &&&& {
                    display: none;
                }
            }
        `,
    },
    partStyles: {
        heading: {
            size: 14,
            weight: 600,
            lineHeight: 1.71,

            css: css`
                display: inline;
                padding-right: 4px;
            `,
        },
        text: {
            size: 14,
            weight: "normal",
            lineHeight: 1.71,

            css: css`
                display: inline;
            `,
        },
        wrapper: {
            css: css`
                display: block;
            `,
        },
    },
    myPartItem: {
        width: 12,
    },
    myPartStyles: {
        heading: {
            size: 14,
            weight: 600,
            lineHeight: 1.71,

            css: css`
                display: inline;
                padding-right: 4px;
            `,
        },
        text: {
            size: 14,
            weight: "normal",
            lineHeight: 1.71,

            css: css`
                display: inline;
            `,
        },
        wrapper: {
            css: css`
                display: block;
            `,
        },
    },
    mgfItem: {
        width: 12,
        css: css`
            ${mediaQueriesBP.maxMD} {
                padding: 0 15px;
            }
        `,
    },
    mgfStyles: {
        heading: {
            size: 14,
            weight: 600,
            lineHeight: 1.71,

            css: css`
                display: inline;
                padding-right: 4px;
            `,
        },
        text: {
            size: 14,
            weight: "normal",
            lineHeight: 1.71,

            css: css`
                display: inline;
            `,
        },
        wrapper: {
            css: css`
                display: block;
            `,
        },
    },
    notesItem: {
        width: 12,
    },
    infoRightColumn: {
        width: 12,
        css: css`
            width: 100%;
            ${mediaQueriesBP.minLG} {
                flex-basis: calc(100% - 180px);
                max-width: calc(100% - 180px);
            }
        `,
    },
    infoRightColumnContainer: {
        gap: 8,
    },
    priceItem: {
        width: [4, 4, 4, 4, 4],
        printWidth: 5,

        css: css`
            flex-direction: column;

            ${TypographyStyle} {
                font-size: 14px;
                font-weight: normal;
                line-height: 1.6;

                &:first-child {
                    font-weight: 600;
                }
            }

            @media print {
                max-width: inherit;
            }
        `,
    },
    priceStyles: {
        text: {
            weight: "bold",
        },
    },
    vatLabelText: {
        size: 12,
    },
    secondaryVatLabelText: {
        size: 12,
    },
    qtyItem: {
        width: [4, 4, 4, 4, 4],
        printWidth: 3,

        css: css`
            flex-direction: column;

            ${TypographyStyle} {
                font-size: 14px;
                font-weight: normal;
                line-height: 1.71;

                &:first-child {
                    font-weight: 600;
                }
            }

            ${FormInputWrapper} {
                @media print {
                    margin-top: 0 !important;
                }
            }

            input {
                max-width: 80px;

                @media print {
                    border: 0 !important;
                    padding: 0 !important;
                    line-height: normal !important;
                    height: auto !important;
                }
            }

            @media print {
                max-width: inherit;
            }
        `,
    },
    totalItem: {
        width: [4, 4, 4, 4, 4],
        printWidth: 4,

        css: css`
            flex-direction: column;

            ${TypographyStyle} {
                font-size: 14px;
                font-weight: normal;
                line-height: 1.6;

                &:first-child {
                    font-weight: 600;
                }
            }

            @media print {
                max-width: inherit;
            }
        `,
    },
    productNameLink: {
        css: css`
            line-height: normal;

            &:hover {
                ${StyledTypography} {
                    text-decoration: underline;
                }
            }

            ${StyledButton} {
                line-height: normal;
                border: 0 !important;
                text-decoration: none;
            }

            ${StyledTypography} {
                font-family: ${fontFamily};
                font-size: 16px;
                font-weight: bold;
                font-stretch: normal;
                font-style: normal;
                line-height: 1.5;
                letter-spacing: normal;
                border: 0 !important;
                text-decoration: none;
            }
        `,
    },
    productDescriptionText: {
        css: css`
            font-family: ${fontFamily};
            font-size: 16px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.5;
            letter-spacing: normal;
        `,
    },
    savedOrderLineCardAddToListGridItem: {
        width: 12,
        css: css`
            width: 100%;
            justify-content: flex-end;
            ${mediaQueriesBP.minLG} {
                flex-basis: 180px;
                max-width: 180px;
            }
        `,
    },
    productAvailabilityGridItem: {
        width: 12,
        printWidth: 0,

        css: css`
            @media print {
                &&&& {
                    display: none;
                }
            }
        `,
    },
    mainInfoItem: {
        width: 12,
        printWidth: 5,
        css: css`
            flex-direction: column;
            flex-basis: calc(100% - 90px);
            max-width: calc(100% - 90px);
            padding: 0;

            ${mediaQueriesBP.minMD} {
                flex-basis: calc(55% - 170px);
                max-width: calc(55% - 170px);
            }
            ${mediaQueriesBP.minLG} {
                flex-direction: row;
                flex-basis: calc(45% - 170px);
                max-width: calc(45% - 170px);
            }
        `,
    },
    secondaryInfoItem: {
        width: 12,
        printWidth: 5,
        css: css`
            flex-direction: column;
            flex-basis: 100%;
            max-width: 100%;
            padding: 0;

            ${mediaQueriesBP.minMD} {
                flex-basis: 45%;
                max-width: 45%;
            }

            ${mediaQueriesBP.minLG} {
                flex-direction: row;
                flex-basis: 55%;
                max-width: 55%;
            }
        `,
    },

    savedOrderLineCardAddToListButton: {
        css: css`
            ${mediaQueriesBP.maxMD} {
                width: 100%;
            }
        `,
    },

    mobileOnly: {
        printWidth: 12,
        css: css`
            display: none;

            ${mediaQueriesBP.minMD} {
                display: flex;
                min-width: 100%;
                flex-basis: 100%;
                padding: 0;
                flex-direction: column;
            }

            @media print {
                display: block;
                max-width: inherit;
            }
        `,
    },

    desktopOnly: {
        printWidth: 0,

        css: css`
            display: flex;
            min-width: 100%;
            flex-basis: 100%;
            padding: 0;
            flex-direction: column;

            ${mediaQueriesBP.minMD} {
                display: none;
            }

            @media print {
                &&&& {
                    display: none;
                }
            }
        `,
    },

    customerWrapper: {
        css: css`
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            margin-top: 10px;
            margin-bottom: 10px;

            div {
                ${mediaQueriesBP.maxMD} {
                    width: 100%;
                }
            }

            ${mediaQueriesBP.maxMD} {
                width: 100%;
            }

            ${mediaQueriesBP.minLG} {
                flex-direction: row;
            }
        `,
    },

    customerNumber: {
        customerLabel: {
            size: "14px",
            weight: 600,
            css: css`
                min-width: 88px;
            `,
        },
    },

    schedulesShipmentLinkWrapper: {
        css: css`
            min-width: 100%;
            margin-bottom: 10px;
        `,
    },

    schedulesShipmentLink: {
        typographyProps: {
            size: "14px",
            lineHeight: "18px",
            css: css`
                margin-right: 5px;
            `,
        },
    },

    scheduleShipmentLinkMobile: {
        css: css`
            display: inline-flex;
            align-items: center;

            ${mediaQueriesBP.minMD} {
                display: none;
            }
        `,
    },
    cartLineQty: {
        editableQuantityTextField: {
            cssOverrides: {
                formInputWrapper: css`
                    &&& {
                        input {
                            max-width: 90px;
                        }
                    }
                `,
            },
        },
    },
};

// eslint-disable-next-line
/* eslint-disable sonarjs/cognitive-complexity */
const SavedOrderDetailsCartLine: React.FC<Props> = ({
    cart,
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    extendedStyles,
    allowMultipleWishLists,
    enableVat,
    vatPriceDisplay,
    showPricing,
    setAddToListModalIsOpen,
    addToWishList,
    toaster,
    handleScheduleShipmentOpen,
    removeCartLine,
    updateCartLine,
    displayOrder,
    customSettings,
}) => {
    // eslint-disable-next-line
    /* eslint-enable sonarjs/cognitive-complexity */
    const [styles] = React.useState(() => mergeToNew(savedOrderDetailsCartLineStyles, extendedStyles));

    const addToListClickHandler = () => {
        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure ?? "",
        };

        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toaster.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toaster.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const modalOpenHandler = () => {
        handleScheduleShipmentOpen(cartLine);
    };

    // eslint-disable-next-line
    /* eslint-disable sonarjs/no-identical-functions */
    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({
                    cartId: cart.id,
                    cartLineId: cartLine.id,
                    onComplete: () => {
                        displayOrder({
                            cartId: cart.id,
                        });
                    },
                });
            } else {
                updateCartLine({
                    cartId: cart.id,
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                    onSuccess: () => {
                        displayOrder({
                            cartId: cart.id,
                        });
                    },
                });
            }
        }
    };

    const removeScheduleHandler = () => {
        updateCartLine({
            cartId: cart.id,
            cartLine: {
                ...cartLine,
                properties: {
                    ...cartLine.properties,
                    scheduledShipments: "",
                },
            },
            onSuccess: () => {
                displayOrder({
                    cartId: cart.id,
                });
            },
        });
    };
    // eslint-disable-next-line
    /* eslint-enable sonarjs/no-identical-functions */

    let bucketType = "shippingNow";
    if (cartLine.properties["isPartialShipment"] && cartLine.properties["isPartialShipment"].toLowerCase() === "true") {
        bucketType = "partialShipment";
    }
    if (
        cartLine.properties["isBackordered"] &&
        cartLine.properties["isBackordered"].toLowerCase() === "true" &&
        (!cartLine.properties["isPartialShipment"] ||
            cartLine.properties["isPartialShipment"]?.toLowerCase() !== "true")
    ) {
        bucketType = "backordered";
    }
    if (
        cartLine.properties["scheduledShipments"] &&
        cartLine.properties["scheduledShipments"].length > 0 &&
        (!cartLine.properties["isPartialShipment"] ||
            cartLine.properties["isPartialShipment"]?.toLowerCase() !== "true")
    ) {
        bucketType = "scheduledShipments";
    }

    return (
        <GridContainer {...styles.container} data-test-selector={`savedOrderDetails_orderLine_${cartLine.id}`}>
            <GridItem {...styles.imageItem}>
                <Link href={cartLine.productUri} {...styles.imageLink}>
                    <LazyImage src={cartLine.smallImagePath} {...styles.image} />
                </Link>
            </GridItem>
            <GridItem {...styles.mainInfoItem}>
                <GridItem {...styles.infoLeftColumn}>
                    <GridContainer {...styles.infoLeftColumnContainer}>
                        {cartLine.brand && (
                            <GridItem {...styles.brandItem} data-test-selector="cartLine_brand">
                                <ProductBrand
                                    brand={cartLine.brand}
                                    showLogo={false}
                                    extendedStyles={styles.brandStyles}
                                />
                            </GridItem>
                        )}
                        <GridItem {...styles.productNameItem}>
                            {cartLine.productUri && (
                                <Link
                                    {...styles.productNameLink}
                                    href={cartLine.productUri}
                                    data-test-selector="cartLine_product"
                                >
                                    {cartLine.shortDescription}
                                </Link>
                            )}
                            {!cartLine.productUri && (
                                <Typography
                                    as="p"
                                    data-test-selector="cartLine_product"
                                    {...styles.productDescriptionText}
                                >
                                    {cartLine.shortDescription}
                                </Typography>
                            )}
                        </GridItem>
                        <GridItem {...styles.mobileOnly}>
                            <GridItem {...styles.numbersItem}>
                                <GridContainer {...styles.numbersInnerContainer}>
                                    {cartLine.erpNumber && (
                                        <GridItem {...styles.partItem}>
                                            <SmallHeadingAndText
                                                heading={translate("Part #")}
                                                text={cartLine.erpNumber}
                                                extendedStyles={styles.partStyles}
                                            />
                                        </GridItem>
                                    )}

                                    {cartLine.manufacturerItem && (
                                        <GridItem {...styles.mgfItem}>
                                            <SmallHeadingAndText
                                                heading={translate("MFG #")}
                                                text={cartLine.manufacturerItem}
                                                extendedStyles={styles.mgfStyles}
                                            />
                                        </GridItem>
                                    )}

                                    <GridItem {...styles.schedulesShipmentLinkWrapper}>
                                        <ScheduleShipmentRenderComponent
                                            modalOpenHandler={modalOpenHandler}
                                            minOrderSubtotal={customSettings.orderMinimumToScheduleShip}
                                            styles={styles}
                                            hasShipments={cartLine.properties["scheduledShipments"]?.trim().length > 0}
                                            removeShipments={removeScheduleHandler}
                                        />
                                    </GridItem>

                                    {cartLine.properties["scheduledShipments"] &&
                                        cartLine.properties["scheduledShipments"].length > 0 && (
                                            <ScheduleShipmentDetailComponent
                                                cartLineId={cartLine.id}
                                                scheduledShipments={JSON.parse(
                                                    cartLine.properties["scheduledShipments"],
                                                )}
                                                extendedStyles={styles.scheduleShipmentDetail}
                                                variant="condensed"
                                            />
                                        )}

                                    <GridItem {...styles.customerItem}>
                                        <StyledWrapper {...styles.customerWrapper}>
                                            <CustomerNumberComponent
                                                productId={cartLine.productId}
                                                customerNumber={cartLine.properties["customerPartNumber"]}
                                                extendedStyles={styles.customerNumber}
                                            />
                                        </StyledWrapper>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.productAvailabilityGridItem}>
                                <ProductAvailability
                                    productId={cartLine.productId!}
                                    availability={cartLine.availability!}
                                    unitOfMeasure={cartLine.unitOfMeasure}
                                    trackInventory={cartLine.trackInventory}
                                    failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                    extendedStyles={styles.productAvailability}
                                    qtyOnHand={cartLine.qtyOnHand}
                                />
                            </GridItem>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridItem>
            <GridItem {...styles.desktopOnly}>
                <GridItem {...styles.numbersItem}>
                    <GridContainer {...styles.numbersInnerContainer}>
                        {cartLine.erpNumber && (
                            <GridItem {...styles.partItem}>
                                <SmallHeadingAndText
                                    heading={translate("Part #")}
                                    text={cartLine.erpNumber}
                                    extendedStyles={styles.partStyles}
                                />
                            </GridItem>
                        )}

                        {cartLine.manufacturerItem && (
                            <GridItem {...styles.mgfItem}>
                                <SmallHeadingAndText
                                    heading={translate("MFG #")}
                                    text={cartLine.manufacturerItem}
                                    extendedStyles={styles.mgfStyles}
                                />
                            </GridItem>
                        )}
                        <GridItem {...styles.schedulesShipmentLinkWrapper}>
                            <ScheduleShipmentRenderComponent
                                modalOpenHandler={modalOpenHandler}
                                minOrderSubtotal={customSettings.orderMinimumToScheduleShip}
                                styles={styles}
                                hasShipments={cartLine.properties["scheduledShipments"]?.trim().length > 0}
                                removeShipments={removeScheduleHandler}
                            />
                        </GridItem>
                        <GridItem {...styles.customerItem}>
                            <StyledWrapper {...styles.customerWrapper}>
                                <CustomerNumberComponent
                                    productId={cartLine.productId}
                                    customerNumber={cartLine.properties["customerPartNumber"]}
                                    extendedStyles={styles.customerNumber}
                                />
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                </GridItem>

                <GridItem {...styles.productAvailabilityGridItem}>
                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={cartLine.trackInventory}
                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                        extendedStyles={styles.productAvailability}
                        qtyOnHand={cartLine.qtyOnHand}
                    />
                </GridItem>
            </GridItem>
            <GridItem {...styles.secondaryInfoItem}>
                <GridItem {...styles.infoRightColumn}>
                    <GridContainer {...styles.infoRightColumnContainer}>
                        <GridItem {...styles.priceItem}>
                            <SmallHeadingAndText
                                heading={translate("Price")}
                                text={
                                    cartLine?.pricing?.unitNetPriceDisplay +
                                    (cartLine.unitOfMeasureDisplay ? ` ${cartLine.unitOfMeasureDisplay}` : "")
                                }
                                extendedStyles={styles.priceStyles}
                            />
                        </GridItem>
                        <GridItem {...styles.qtyItem}>
                            <CartLineQuantity
                                cart={cart}
                                editable={false}
                                onQtyOrderedChange={qtyOrderedChangeHandler}
                                bucketType={bucketType}
                                extendedStyles={styles.cartLineQty}
                            />
                        </GridItem>
                        <GridItem {...styles.totalItem}>
                            {cartLine.pricing && showPricing && (
                                <>
                                    {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                        <>
                                            <SmallHeadingAndText
                                                heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                                text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                                extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                                            />
                                            <SmallHeadingAndText
                                                heading={`${translate("Total VAT")} (${cartLine.pricing.vatRate}%)`}
                                                text={cartLine.pricing.vatAmountDisplay}
                                                extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                                            />
                                        </>
                                    )}
                                    <SmallHeadingAndText
                                        heading={
                                            !enableVat
                                                ? translate("Subtotal")
                                                : vatPriceDisplay !== "DisplayWithoutVat"
                                                ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                                : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                        }
                                        text={
                                            enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                : cartLine.pricing.extendedUnitNetPriceDisplay
                                        }
                                        extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                                    />
                                </>
                            )}
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.savedOrderLineCardAddToListGridItem}>
                    {cartLine.canAddToWishlist && !cartLine.isRestricted && (
                        <Button
                            {...styles.savedOrderLineCardAddToListButton}
                            data-test-selector={`savedOrderDetails_productLine_${cartLine.erpNumber}`}
                            onClick={addToListClickHandler}
                        >
                            {translate("Add to List")}
                        </Button>
                    )}
                </GridItem>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(withCartLine(SavedOrderDetailsCartLine)));
